/** @jsx jsx */

import { Fragment } from 'react';
import { css, jsx } from '@emotion/core';
import {
  Alert,
  Grid,
  Icon,
  LabeledCheckbox,
  LabeledRadio,
  Text,
  TextInput,
  TooltipWrapper,
} from '@zapier/design-system';
import { fontFamilyMonospace } from 'app/developer-v3/components/AppBuilder/AppBuilder.styles';
import { renderMarkdown } from 'app/developer-v3/utils/markdown';
import { snakeCase } from 'lodash';

const styleIndent = css`
  padding-left: 35px;
`;

const styleContainer = css`
  /* Stop line wrapping in bad locations */
  &&&&&& code {
    word-break: unset;
  }

  &[data-need-type='code'] {
    textarea[class][class][class][class][class][class][class] {
      font-family: ${fontFamilyMonospace};
    }
  }
`;

function toScreamingSnakeCase(str: string): string {
  return snakeCase(str).toUpperCase();
}

export function createPlaceholder(str: string): string {
  return toScreamingSnakeCase(str);
}

export interface ZapierActionNeeds {
  key: string;
  type: string;
  computed?: boolean;
  prefill: string;
  label: string;
  help_text: string | null;
  list: boolean;
  required: boolean;
  placeholder: string;
}

export interface NeedsItemEditorProps {
  isEnabled: boolean;
  need: ZapierActionNeeds;
  onChange: (value: string | undefined) => void;
  value: string | undefined;
  step: 0 | 1;
}

export function NeedsItemEditor({
  isEnabled,
  need,
  onChange,
  step,
  value,
}: NeedsItemEditorProps): JSX.Element {
  const labelID = `NeedsItemEditor--${need.key}--label`;
  return (
    <div css={styleContainer} data-need-type={need.type}>
      {(() => {
        // Can add special handling if/when list prefills are supported, but for now we can at least allow one value
        // if (need.list) {
        //   // Just show the label
        //   return (
        //     <div>
        //       <NeedsItemCheckbox
        //         disabled={true}
        //         isEnabled={isEnabled}
        //         labelID={labelID}
        //         need={need}
        //         onChange={onChange}
        //         value={value}
        //       />
        //     </div>
        //   );
        // }
        switch (need.type) {
          case 'string': // Single-line text
          case 'integer':
          case 'number':
          case 'datetime':
          case 'password':
          case 'text': // Multi-line text
          case 'code': {
            return (
              <div>
                <NeedsItemCheckbox
                  disabled={false}
                  isEnabled={isEnabled}
                  labelID={labelID}
                  need={need}
                  onChange={onChange}
                  value={value}
                />
                {isEnabled && (
                  <div css={styleIndent}>
                    <TextInput
                      aria-labelledby={labelID}
                      isMultiline={need.type === 'text' || need.type === 'code'}
                      onChange={event => {
                        const elem = event.currentTarget as
                          | HTMLInputElement
                          | HTMLTextAreaElement;
                        onChange(elem.value);
                      }}
                      placeholder={createPlaceholder(
                        [step === 0 ? 'trigger' : 'action', need.key].join(' ')
                      )}
                      value={value}
                    />
                    {need.help_text && (
                      <Text>
                        <div
                          dangerouslySetInnerHTML={{
                            __html: renderMarkdown(need.help_text || ''),
                          }}
                        />
                      </Text>
                    )}
                  </div>
                )}
              </div>
            );
          }
          case 'boolean': {
            return (
              <div>
                <NeedsItemCheckbox
                  disabled={false}
                  isEnabled={isEnabled}
                  labelID={labelID}
                  need={need}
                  onChange={onChange}
                  value={value}
                />
                {isEnabled && (
                  <div css={styleIndent}>
                    <Grid columns="repeat(2, max-content)" gap={10}>
                      <LabeledRadio
                        checked={value === 'true'}
                        name={need.key}
                        onChange={() => {
                          onChange('true');
                        }}
                        tabIndex={0}
                      >
                        True
                      </LabeledRadio>
                      <LabeledRadio
                        checked={value !== 'true'}
                        name={need.key}
                        onChange={() => {
                          onChange('false');
                        }}
                        tabIndex={0}
                      >
                        False
                      </LabeledRadio>
                    </Grid>
                    {need.help_text && (
                      <Text>
                        <div
                          dangerouslySetInnerHTML={{
                            __html: renderMarkdown(need.help_text || ''),
                          }}
                        />
                      </Text>
                    )}
                  </div>
                )}
              </div>
            );
          }
          case 'copy': {
            return (
              <div css={{ margin: '20px 0' }}>
                <Alert>
                  <div
                    dangerouslySetInnerHTML={{
                      __html: renderMarkdown(need.help_text || ''),
                    }}
                  />
                </Alert>
              </div>
            );
          }
          default: {
            // Just show the label
            return (
              <div>
                <NeedsItemCheckbox
                  disabled={true}
                  isEnabled={isEnabled}
                  labelID={labelID}
                  need={need}
                  onChange={onChange}
                  value={value}
                />
              </div>
            );
          }
        }
      })()}
    </div>
  );
}
function NeedsItemCheckbox({
  need,
  isEnabled,
  disabled,
  labelID,
  onChange,
  value,
}: {
  need: ZapierActionNeeds;
  isEnabled: boolean;
  disabled: boolean;
  labelID: string;
  onChange: (value: string) => void;
  value: string;
}) {
  return (
    <Fragment>
      <TooltipWrapper
        allowMultilineTooltip={true}
        content={
          disabled
            ? 'Complex field value types cannot be pre-filled'
            : undefined
        }
        position="east"
      >
        {() => (
          <LabeledCheckbox
            checked={isEnabled}
            disabled={disabled}
            id={labelID}
            // Why not use `isRequired`? The CSS is messed up right now and it
            // looks bad. Hopefully upgrading Zinnia will fix the issue.
            // (2023-06-01)
            key={need.key}
            onChange={event => {
              const elem = event.currentTarget as HTMLInputElement;
              if (elem.checked) {
                if (need.type === 'boolean') {
                  onChange(value ? 'true' : 'false');
                } else {
                  onChange(value || '');
                }
              } else {
                onChange(undefined);
              }
            }}
            tabIndex={0}
          >
            <span>
              {need.required && '* '}
              {need.label || `[${need.key}]`}
            </span>
          </LabeledCheckbox>
        )}
      </TooltipWrapper>
      {need.required && (
        <TooltipWrapper
          allowMultilineTooltip={true}
          content="This is a required field when your users create this Zap. Pre-fill this field when possible to help them complete the Zap."
          position="southwest"
        >
          {() => <Icon name="alertInfo" size={20} />}
        </TooltipWrapper>
      )}
    </Fragment>
  );
}
